import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Collapse, Tooltip } from '@mui/material';
import Map, {
  NavigationControl,
  FullscreenControl,
  Marker,
  Source,
  Layer,
} from 'react-map-gl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft as ArrowLeftIcon } from '@fortawesome/free-solid-svg-icons';
import { faChevronRight as ArrowRightIcon } from '@fortawesome/free-solid-svg-icons';
import mapboxgl, { MapboxEvent } from 'mapbox-gl';
import { Feature, Polygon } from 'geojson';
import * as turf from '@turf/turf';
import { useSnackbar } from 'notistack';

import MarkerWarehouse from '~components/MarkerWarehouse';
import {
  ModeSateliteControl,
  ShowTripViewControl,
  LayersControl,
  SelectionOrderControl,
  SelectionControl,
} from '~components/MapCustomControl';
import {
  Layers as LayerControlKey,
  LayersSelector,
} from '~components/MapCustomControl/LayersControl';
import { SelectionControlFeatures } from '~components/MapCustomControl/SelectionControl/types';
import { Marker as MarkerIcon } from '~components/Icons';
import {
  useToggle,
  useLazyRequest,
  useRequest,
  useSubscriptionFeatures,
  useAppSelector,
} from '~hooks/index';
import {
  usePopupRequest,
  PopupRequestOpenPopupHandler,
} from '~hooks/usePopupRequest';
import { getItemInfo } from '~services/item';
import { getWarehouseInfo } from '~services/warehouse';
import { searchRoutes } from '~services/route';
import { RouteFilter, SearchRoute, RouteStats } from '~services/route/types';
import { getLogistiscZonesMap } from '~services/logisticsZones';
import { TOKEN } from '~utils/mapbox';
import { isOnlyCreatorOrder } from '~utils/user';
import { createFeature } from '~utils/geoJson';
import { MapControlBaseEventHandler } from '~utils/mapClasses/ControlBar';
import { controlLocale } from '~constants/mapControlLocale';
import {
  MapTripData,
  PaginationRequestParams,
  DataPaginateResponse,
  ErrorResponse,
} from '~globals/types';
import {
  PlanFeaturesTypes,
  RouteStateTypes,
  LogisticZoneType,
} from '~globals/types/enums';
import { selectAuth } from '~store/slices/auth';

import { useDashboardVariantV2CarrierContentInWarehouseTabContext } from '../../Context';
import {
  DashboardVariantV2CarrierContentMapLoading,
  DashboardVariantV2CarrierContentMapContainer,
  DashboardVariantV2CarrierContentMapToggleFullWidthButton,
} from '../../../../styles';

import DashboardVariantV2CarrierContentInWarehouseTabMapPopup from './Popup';
import { getRouteTripCoords, getDefaultPayloadFetchRoutes } from './utils';

const DashboardVariantV2CarrierContentInWarehouseTabMap = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasAccessToFeature } = useSubscriptionFeatures();
  const { user } = useAppSelector(selectAuth);

  const isUserOnlyCreatorOrder = useMemo(
    () => isOnlyCreatorOrder(user),
    [user],
  );

  const {
    selection: { utils: selectedIdsUtils },
    map: {
      showMap: { open: showMap },
      data: {
        orders: { data: orders, loading: loadingOrders },
        warehouses: { data: warehouses, loading: loadingWarehouses },
      },
    },
    table: {
      showTable: { open: showTable, toggleOpen: toggleShowTable },
    },
    dialogs: {
      detailsOrder: { handleOpen: handleOpenDialogDetailsOrder },
    },
  } = useDashboardVariantV2CarrierContentInWarehouseTabContext();

  const bounds = useMemo(() => {
    const boundsInstance = new mapboxgl.LngLatBounds();

    orders.forEach((order) => {
      if (order.coordinates.length > 1) {
        boundsInstance.extend(order.coordinates);
      }
    });

    warehouses.forEach((warehouse) => {
      if (warehouse.coordinates.length > 1) {
        boundsInstance.extend(warehouse.coordinates);
      }
    });

    return boundsInstance;
  }, [orders, warehouses]);

  const { handleOpenPopup: handleOpenPopupOrder, ...restPopupOrder } =
    usePopupRequest({
      request: getItemInfo,
    });

  const { handleOpenPopup: handleOpenPopupWarehouse, ...restPopupWarehouse } =
    usePopupRequest({
      request: getWarehouseInfo,
    });

  const [enabledSelectedMarkers, toggleEnabledSelectedMarkers] =
    useToggle(false);

  const [isDrawing, toggleIsDrawing] = useToggle(false);

  const selectedCapacities = useMemo(
    () =>
      orders
        .filter((order) => selectedIdsUtils.has(order.itemId))
        .map((order) => ({
          capacities: { primary: order.units[0], secondary: order.units[1] },
        })),
    [orders, selectedIdsUtils],
  );

  const markerCursor = useMemo(
    () => (enabledSelectedMarkers ? 'pointer' : 'grab'),
    [enabledSelectedMarkers],
  );

  const getCurrentColorMarker = useCallback(
    (markerId: string) => (selectedIdsUtils.has(markerId) ? '#5618b6' : '#000'),
    [selectedIdsUtils],
  );

  const handleCreateDraw = useCallback<
    MapControlBaseEventHandler<SelectionControlFeatures>
  >(
    (event) => {
      const selectedPolygon = event.features[0];

      const drawSelectedIds: string[] = [];

      for (const order of orders) {
        if (order.coordinates.length > 1) {
          const isSelectedOnPolygon = turf.booleanPointInPolygon(
            order.coordinates,
            selectedPolygon,
          );

          if (isSelectedOnPolygon) {
            drawSelectedIds.push(order.itemId);
          }
        }
      }

      selectedIdsUtils.addMultiple(drawSelectedIds);

      setTimeout(() => toggleIsDrawing(false), 0);
    },
    [orders, selectedIdsUtils, toggleIsDrawing],
  );

  const handleClickOrderMarker = useCallback<PopupRequestOpenPopupHandler>(
    async (event, id, coordinates) => {
      if (isDrawing) return;

      if (enabledSelectedMarkers) {
        selectedIdsUtils.toggle(id);
      } else {
        await handleOpenPopupOrder(event, id, coordinates);
      }
    },
    [enabledSelectedMarkers, handleOpenPopupOrder, isDrawing, selectedIdsUtils],
  );

  const handleClickOrderWarehouse = useCallback<PopupRequestOpenPopupHandler>(
    async (event, id, coordinates) => {
      if (isDrawing) return;

      await handleOpenPopupWarehouse(event, id, coordinates);
    },
    [handleOpenPopupWarehouse, isDrawing],
  );

  const handleSelectAllOnMapSelection = useCallback(() => {
    const ordersIds = orders.map((order) => order.itemId);

    selectedIdsUtils.addMultiple(ordersIds);
  }, [orders, selectedIdsUtils]);

  const [logisticsZones, setLogisticsZone] = useState<Feature<Polygon>[]>([]);

  const isValidToFetcLogisticzZoneS = useMemo(
    () => hasAccessToFeature(PlanFeaturesTypes.MgmZones),
    [hasAccessToFeature],
  );

  const [, loadingLogisticsZones, , reloadLogisiticZones] = useRequest({
    request: getLogistiscZonesMap,
    isValidToRequest: isValidToFetcLogisticzZoneS,
    initialLoading: isValidToFetcLogisticzZoneS,
    payload: null,
    withPostSuccess: (response) => {
      const currentLogisitcsZones = response.data?.data.results;

      if (currentLogisitcsZones) {
        const filtersGeometryAreaLogisitcsZones = currentLogisitcsZones.filter(
          (zone) => zone.zoneTypeId === LogisticZoneType.GeometryArea,
        );

        const featuresPolygonsLogisitcsZones =
          filtersGeometryAreaLogisitcsZones.map((zone) => {
            const currentPolygon: Polygon = JSON.parse(
              zone.geometryAreaJson as string,
            );

            return createFeature({
              geometry: currentPolygon,
              id: zone.zoneId,
              properties: {
                currentColor: zone.color,
                label: zone.code,
              },
            });
          });

        setLogisticsZone(featuresPolygonsLogisitcsZones);
      }
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
    },
  });

  const [pendingRoutesTrip, setPendingRoutesTrip] = useState<MapTripData[]>([]);

  const [, loadingPendingRoutes, , executeFetchPendingRoutes] = useLazyRequest<
    PaginationRequestParams<RouteFilter<true>>,
    DataPaginateResponse<SearchRoute<true>[], RouteStats>,
    ErrorResponse
  >({
    request: searchRoutes,
    withPostSuccess: (response) => {
      const {
        data: { results: pendingRoutes },
      } = response.data as DataPaginateResponse<
        SearchRoute<true>[],
        RouteStats
      >;

      pendingRoutes.forEach((route) =>
        route.routeItems.sort((a, b) => a.order - b.order),
      );

      const newPendingRoutesTrip: MapTripData[] = pendingRoutes.map(
        (route) => ({
          id: `pending-route-trip-${route.routeId}`,
          coordinates: getRouteTripCoords(route.routeItems),
          color: route?.vehicle.color ?? '',
        }),
      );

      setPendingRoutesTrip(newPendingRoutesTrip);
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
    },
  });

  const [inProgressRoutesTrip, setInProgressRoutesTrip] = useState<
    MapTripData[]
  >([]);

  const [, loadingInProgressRoutes, , executeFetchInProgressRoutes] =
    useLazyRequest<
      PaginationRequestParams<RouteFilter<true>>,
      DataPaginateResponse<SearchRoute<true>[], RouteStats>,
      ErrorResponse
    >({
      request: searchRoutes,
      withPostSuccess: (response) => {
        const {
          data: { results: inProgressRoutes },
        } = response.data as DataPaginateResponse<
          SearchRoute<true>[],
          RouteStats
        >;

        inProgressRoutes.forEach((route) =>
          route.routeItems.sort((a, b) => a.order - b.order),
        );

        const newInProgressRoutesTrip: MapTripData[] = inProgressRoutes.map(
          (route) => ({
            id: `in-progress-route-trip-${route.routeId}`,
            coordinates: getRouteTripCoords(route.routeItems),
            color: route?.vehicle.color ?? '',
          }),
        );

        setInProgressRoutesTrip(newInProgressRoutesTrip);
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
    });

  const onToggleLayerControl = useCallback(
    (layerKey: LayerControlKey, checked: boolean) => {
      if (layerKey === 'zones') {
        if (checked) {
          reloadLogisiticZones(null);
        } else {
          setLogisticsZone([]);
        }
      }

      if (layerKey === 'routesOnAgenda') {
        if (checked) {
          executeFetchPendingRoutes(
            getDefaultPayloadFetchRoutes([
              RouteStateTypes.New,
              RouteStateTypes.Assigned,
              RouteStateTypes.Approve,
            ]),
          );
        } else {
          setPendingRoutesTrip([]);
        }
      }

      if (layerKey === 'routesInProgress') {
        if (checked) {
          executeFetchInProgressRoutes(
            getDefaultPayloadFetchRoutes([RouteStateTypes.InProgress]),
          );
        } else {
          setInProgressRoutesTrip([]);
        }
      }
    },
    [
      reloadLogisiticZones,
      executeFetchPendingRoutes,
      executeFetchInProgressRoutes,
    ],
  );

  const generaloading = useMemo(
    () => loadingOrders || loadingWarehouses,
    [loadingOrders, loadingWarehouses],
  );

  const [fullWidth, setFullWidth] = useState(!showTable);

  useEffect(() => {
    setTimeout(() => setFullWidth(!showTable), 300);
  }, [showTable]);

  const [map, setMap] = useState<MapboxEvent<undefined>>();

  const onResize = useCallback(() => {
    if (map) {
      map.target.resize();
    }
  }, [map]);

  useEffect(() => {
    onResize();
  }, [fullWidth, onResize]);

  return (
    <Collapse orientation="horizontal" in={showMap}>
      {generaloading && (
        <DashboardVariantV2CarrierContentMapLoading type="relative" />
      )}

      {!generaloading && (
        <DashboardVariantV2CarrierContentMapContainer fullWidth={fullWidth}>
          {(loadingLogisticsZones ||
            loadingPendingRoutes ||
            loadingInProgressRoutes) && (
            <DashboardVariantV2CarrierContentMapLoading type="absolute" />
          )}

          <LayersControl
            hiddenMarkers
            hiddenTrips
            hiddenVehicles
            hiddenRouteItemState
            hiddenOrders={false}
            hiddenWarehouses={false}
            hiddenRoutesOnAgenda={isUserOnlyCreatorOrder}
            hiddenRoutesInProgress={isUserOnlyCreatorOrder}
            hiddenZones={false}
            onToggle={onToggleLayerControl}
          >
            {({
              enabledOrders,
              enabledWarehouses,
              enabledZones,
              enabledRoutesOnAgenda,
              enabledRoutesInProgress,
              checkedLayers,
              layersList,
              toggleLayer,
            }) => (
              <Map
                mapboxAccessToken={TOKEN}
                mapStyle="mapbox://styles/mapbox/light-v10"
                initialViewState={{
                  bounds,
                  fitBoundsOptions: {
                    padding: 100,
                    maxZoom: 12,
                  },
                }}
                attributionControl={false}
                locale={controlLocale}
                onLoad={setMap}
              >
                <FullscreenControl position="bottom-left" />
                <NavigationControl position="bottom-left" showCompass={false} />
                <ShowTripViewControl
                  position="bottom-left"
                  currentBounds={bounds}
                />
                <ModeSateliteControl position="bottom-left" />

                <SelectionControl
                  position="top-right"
                  actions={{
                    onInitalizeDraw: () => toggleIsDrawing(true),
                    onDraw: handleCreateDraw,
                    onFreHand: () => toggleEnabledSelectedMarkers(false),
                    onSelectedMarkers: () => toggleEnabledSelectedMarkers(),
                  }}
                />

                <SelectionOrderControl
                  orders={selectedCapacities}
                  totalOrdersCount={orders.length}
                  onSelectAll={handleSelectAllOnMapSelection}
                  onClear={() => selectedIdsUtils.reset()}
                />

                <LayersSelector
                  checkedLayers={checkedLayers}
                  layersList={layersList}
                  toggleLayer={toggleLayer}
                  validSubscriptionFeature={{
                    routesOnAgenda: {
                      validFeature: PlanFeaturesTypes.LimitNotPro,
                      availableToDate: undefined,
                    },
                    zones: {
                      validFeature: PlanFeaturesTypes.MgmZones,
                    },
                  }}
                />

                <Tooltip
                  arrow
                  placement="right"
                  title={
                    fullWidth
                      ? 'Mostrar lista de pedidos'
                      : 'Ocultar lista de pedidos'
                  }
                >
                  <DashboardVariantV2CarrierContentMapToggleFullWidthButton
                    variant="contained"
                    onClick={() => toggleShowTable()}
                  >
                    <FontAwesomeIcon
                      icon={fullWidth ? ArrowRightIcon : ArrowLeftIcon}
                      style={{ width: 20, height: 20 }}
                    />
                  </DashboardVariantV2CarrierContentMapToggleFullWidthButton>
                </Tooltip>

                {enabledOrders &&
                  orders.map(
                    (orderMarker) =>
                      orderMarker.coordinates.length > 1 && (
                        <Marker
                          key={`marker-order-${orderMarker.itemId}`}
                          longitude={orderMarker.coordinates[0]}
                          latitude={orderMarker.coordinates[1]}
                          onClick={(e) =>
                            handleClickOrderMarker(
                              e,
                              orderMarker.itemId,
                              orderMarker.coordinates,
                            )
                          }
                          anchor="bottom"
                          style={{ cursor: markerCursor }}
                        >
                          <MarkerIcon
                            htmlColor={getCurrentColorMarker(
                              orderMarker.itemId,
                            )}
                          />
                        </Marker>
                      ),
                  )}

                <DashboardVariantV2CarrierContentInWarehouseTabMapPopup
                  {...restPopupOrder}
                  isItem
                  onShowDetailOrder={handleOpenDialogDetailsOrder}
                />

                {enabledWarehouses &&
                  warehouses.map(
                    (warehouseMarker) =>
                      warehouseMarker.coordinates.length > 1 && (
                        <Marker
                          key={`marker-warehouse-${warehouseMarker.warehouseId}`}
                          longitude={warehouseMarker.coordinates[0]}
                          latitude={warehouseMarker.coordinates[1]}
                          onClick={(e) =>
                            handleClickOrderWarehouse(
                              e,
                              warehouseMarker.warehouseId,
                              warehouseMarker.coordinates,
                            )
                          }
                          anchor="bottom"
                        >
                          <MarkerWarehouse
                            color="#00a200"
                            includeBackgroundColor
                          />
                        </Marker>
                      ),
                  )}

                <DashboardVariantV2CarrierContentInWarehouseTabMapPopup
                  {...restPopupWarehouse}
                />

                {enabledZones &&
                  logisticsZones.map((zone) => (
                    <Source
                      key={zone.id}
                      id={`zone-${zone.id}`}
                      type="geojson"
                      data={zone}
                    >
                      <Layer
                        id={`zone-${zone.id}-fill`}
                        type="fill"
                        paint={{
                          'fill-color': ['get', 'currentColor'],
                          'fill-outline-color': ['get', 'currentColor'],
                          'fill-opacity': 0.1,
                        }}
                      />

                      <Layer
                        id={`zone-${zone.id}-line`}
                        type="line"
                        layout={{
                          'line-join': 'round',
                          'line-cap': 'round',
                        }}
                        paint={{
                          'line-color': ['get', 'currentColor'],
                          'line-width': 2,
                        }}
                      />

                      <Layer
                        id={`zone-${zone.id}-label`}
                        type="symbol"
                        layout={{
                          'text-field': ['get', 'label'],
                          'text-size': 12,
                          'text-offset': [0, 1.25],
                          'text-anchor': 'center',
                        }}
                        paint={{
                          'text-color': ['get', 'currentColor'],
                        }}
                      />
                    </Source>
                  ))}

                {enabledRoutesOnAgenda &&
                  pendingRoutesTrip.map((trip) => (
                    <Source
                      key={trip.id}
                      id={trip.id}
                      type="geojson"
                      data={{
                        type: 'Feature',
                        properties: {},
                        geometry: {
                          type: 'LineString',
                          coordinates: trip.coordinates,
                        },
                      }}
                    >
                      <Layer
                        id={trip.id}
                        type="line"
                        layout={{
                          'line-join': 'round',
                          'line-cap': 'round',
                        }}
                        paint={{
                          'line-color': trip.color,
                          'line-width': 2,
                        }}
                      />
                      <Layer
                        id={`${trip.id}-trip-arrow`}
                        type="symbol"
                        layout={{
                          'symbol-placement': 'line',
                          'text-field': '▶',
                          'text-size': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            12,
                            10,
                            22,
                            60,
                          ],
                          'symbol-spacing': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            12,
                            15,
                            22,
                            160,
                          ],
                          'text-keep-upright': false,
                        }}
                        paint={{
                          'text-color': trip.color,
                        }}
                      />
                    </Source>
                  ))}

                {enabledRoutesInProgress &&
                  inProgressRoutesTrip.map((trip) => (
                    <Source
                      key={trip.id}
                      id={trip.id}
                      type="geojson"
                      data={{
                        type: 'Feature',
                        properties: {},
                        geometry: {
                          type: 'LineString',
                          coordinates: trip.coordinates,
                        },
                      }}
                    >
                      <Layer
                        id={trip.id}
                        type="line"
                        layout={{
                          'line-join': 'round',
                          'line-cap': 'round',
                        }}
                        paint={{
                          'line-color': trip.color,
                          'line-width': 2,
                        }}
                      />
                      <Layer
                        id={`${trip.id}-trip-arrow`}
                        type="symbol"
                        layout={{
                          'symbol-placement': 'line',
                          'text-field': '▶',
                          'text-size': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            12,
                            10,
                            22,
                            60,
                          ],
                          'symbol-spacing': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            12,
                            15,
                            22,
                            160,
                          ],
                          'text-keep-upright': false,
                        }}
                        paint={{
                          'text-color': trip.color,
                        }}
                      />
                    </Source>
                  ))}
              </Map>
            )}
          </LayersControl>
        </DashboardVariantV2CarrierContentMapContainer>
      )}
    </Collapse>
  );
};

export default DashboardVariantV2CarrierContentInWarehouseTabMap;
